import {Action, Card, DataStatus} from 'platform/components';
import {Box} from 'platform/foundation';

import {useState} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTireIdsQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {tiresInventoryRoutes} from '@dms/routes';
import {DmsDataGrid, TireSetDetail} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface TireSetsProps {
  vehicleId: string;
  customerId: string;
}

const PREDEFINED_COLUMNS = [
  'tireManufacturer',
  'tireSeason',
  'tireOrderId',
  'id',
  'setNumber',
] as const;

export function TireSets(props: TireSetsProps) {
  const navigate = useNavigate();
  const [editTireSetData, setEditTireSetData] = useState<{
    orderId: string;
    setId: string;
    title: string;
    refreshData: () => void;
  } | null>(null);

  const tireOrderIdsQuery = useGetTireIdsQuery({
    customerId: props.customerId,
    vehicleId: props.vehicleId,
  });

  const firstTireOrder = tireOrderIdsQuery.data?.[0];
  const hasTireOrder = isNotNilOrEmpty(firstTireOrder);

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {
        vehicleId: props.vehicleId ? props.vehicleId : undefined,
      },
    ]);

  const actions = buildArray<Action>()
    .when(hasTireOrder, {
      type: 'button',
      variant: 'link',
      leftIcon: 'action/open_in_new',
      title: i18n.t('page.tiresInventory.labels.tiresInventory'),
      onClick: () => {
        window.open(
          composePath(tiresInventoryRoutes.tireOrderDetail, {
            params: {id: tireOrderIdsQuery.data?.[0]?.orderId},
            isSearchParamsPreserved: false,
          }),
          '_blank'
        );
      },
    })
    .whenNot(hasTireOrder, {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/add_circle',
      title: i18n.t('page.tiresInventory.labels.createTireOrder'),
      onClick: () => {
        window.open(
          composePath(tiresInventoryRoutes.createTireOrder, {
            queryParams: {
              customerId: props.customerId,
              vehicleId: props.vehicleId,
            },
            isSearchParamsPreserved: false,
          }),
          '_blank'
        );
      },
    });

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const tireManufacturer = getColumnData<{label: string}>('tireManufacturer')?.value?.label;
    const tireSeason = getColumnData<{label: string}>('tireSeason')?.value?.label;
    const orderId = getColumnData<string>('tireOrderId')?.value;
    const orderSetId = Array.isArray(rowId) ? rowId[0] : rowId;
    const setNumber = getColumnData<string>('setNumber')?.value;

    if (actionKey === 'redirectDetail') {
      const titleData = [tireManufacturer, tireSeason].filter(isNotNilOrEmpty);

      if (isNil(orderId) || isNil(orderSetId)) {
        throw new Error('orderId or orderSetId is undefined');
      }

      setEditTireSetData({
        orderId,
        setId: orderSetId,
        title: `${setNumber} - ${titleData.join(', ')}`,
        refreshData,
      });
    }

    if (actionKey === 'redirectTireOrder') {
      navigate(
        composePath(tiresInventoryRoutes.tireOrderDetail, {
          params: {id: orderId},
          queryParams: {source: 'tire-list'},
        })
      );
    }
  };

  return (
    <DataStatus isLoading={tireOrderIdsQuery.isLoading}>
      <Box>
        <Card title={i18n.t('page.tiresInventory.labels.tireSets')} actions={actions}>
          <DmsDataGrid
            data-testid="tireSets-grid"
            gridCode="tire-sets-service-case"
            autoHeight
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            queryModifier={queryModifier}
          />
        </Card>
      </Box>

      {editTireSetData && (
        <TireSetDetail
          tireOrderId={editTireSetData.orderId}
          tireSetId={editTireSetData.setId}
          onClose={() => setEditTireSetData(null)}
          onEdit={() => {
            editTireSetData.refreshData();
            setEditTireSetData(null);
          }}
        />
      )}
    </DataStatus>
  );
}
